import React, { useEffect, memo, useState, useRef } from 'react';
import { uniq } from 'lodash';
import { connect } from 'react-redux';
import { selectCurrentDisplayedIncident } from '../../../../state/modules/incidents/selectors';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import ElapsedTime from './ElapsedTime';
import Sound from 'react-sound';
import Settings from './Settings';
// http://www.e2s.com/information/listen-and-download-alarm-tones
import tone from './tone.mp3';
import { Badge } from '../../../ui-kit';
import moment from 'moment';

const prioToStyle = {
  prio1: 'danger',
  prio2: 'warning',
  prio3: 'secondary'
};

const addressStyle = {
  width: 'calc(100% - 280px)'
};

const TextToSpeech = ({ messageToSpeechFile, repeatCount }) => {
  const [state, setState] = useState({
    playTextToSpeech: false,
    playAlert: true,
    playCount: 0
  });

  const playAlertFinished = () => {
    setState({
      ...state,
      // after playing the alert tone, play the text to speech
      playTextToSpeech: true,
      playAlert: false
    });
  };

  const playTextFinished = () => {
    // if looping, restart alert, which will then play speech again, for a
    // maximum of repeatCount
    setState({
      playAlert: state.playCount + 1 < repeatCount,
      playTextToSpeech: false,
      playCount: state.playCount + 1
    });
  };

  const handleError = err => {
    console.log('Error while playing sound', err);
    playAlertFinished();
  };

  if (!messageToSpeechFile) return null;

  return (
    <>
      <Sound
        url={tone}
        playStatus={state.playAlert ? Sound.status.PLAYING : Sound.status.STOPPED}
        onError={handleError}
        onFinishedPlaying={playAlertFinished}
      />
      <Sound
        url={messageToSpeechFile}
        playStatus={state.playTextToSpeech ? Sound.status.PLAYING : Sound.status.STOPPED}
        onFinishedPlaying={playTextFinished}
      />
    </>
  );
};

const IncidentMessage = ({ displayedIncident, textToSpeech, timeLimit, timeLimitCheck, textToSpeechRepeatCount }) => {
  const [spokenIncidentIds, setSpokenIncidentIds] = useState([]);
  const previousIncidentId = useRef(null);

  // When the currently displayed incident changes (i.e. there is more than 1
  // incident), mark the incident as spoken. This is to prevent incidents from
  // being spoken repeatedly, causing annoyance with users.
  useEffect(() => {
    if (!displayedIncident) return;
    if (displayedIncident.id === previousIncidentId.current) return;
    // adds the incident id to the spoken incident ids, to prevent playing the
    // sound everytime the incident becomes active
    setSpokenIncidentIds(uniq([...spokenIncidentIds, previousIncidentId.current]));
    previousIncidentId.current = displayedIncident.id;
  }, [displayedIncident?.id]);

  if (!displayedIncident) return <WaitingForIncidentMessage />;

  // desired behaviour for audio playback:
  // Play alert sound, once it finishes, play text to speech if setting is
  // enabled.
  // If loop is enabled, replay text to speech a couple of times if there are
  // multiple active incidents, only the first time it's shown should emit the
  // sound.
  const hasIncidentBeenSpoken = spokenIncidentIds.includes(displayedIncident.id);
  const shouldPlaySound = textToSpeech && !hasIncidentBeenSpoken;
  const timeLimitBoundary = moment(displayedIncident.created_at).add(timeLimit, 'seconds');
  const effectiveTimeLimit = timeLimitCheck ? timeLimitBoundary : null;

  return (
    <>
      {shouldPlaySound && (
        <TextToSpeech
          key={displayedIncident.message_to_speech_url}
          messageToSpeechFile={displayedIncident.message_to_speech_url}
          repeatCount={textToSpeechRepeatCount}
        />
      )}
      <ElapsedTime
        startTime={displayedIncident.created_at}
        timeLimit={effectiveTimeLimit}
        incidentId={displayedIncident?.id}
      />
      <h2 className="fw-bold">
        <Badge variant={prioToStyle[displayedIncident.prio]} style={{ verticalAlign: 'top' }}>
          {displayedIncident.prio}
        </Badge>{' '}
        {displayedIncident.body}
      </h2>
      {displayedIncident.address && <h3 style={addressStyle}>{displayedIncident.address.formatted_address}</h3>}
    </>
  );
};

const mapStateToProps = state => ({
  displayedIncident: selectCurrentDisplayedIncident(state)
});

const IncidentMessageMemo = memo(IncidentMessage);

Object.assign(IncidentMessageMemo, {
  SettingsContent: Settings,
  defaultSettings: {
    textToSpeech: false,
    textToSpeechRepeatCount: 1,
    timeLimitCheck: false,
    timeLimit: 300 // 5 minutes default
  }
});

export default connect(mapStateToProps)(IncidentMessageMemo);
